/**
 * Stat Card Component
 *
 * Reusable card component for displaying statistics on dashboards
 *
 * @module components/common/StatCard
 */

import React from 'react';
import { Card } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * StatCard Component
 *
 * @param {Object} props
 * @param {string} props.title - Card title/label
 * @param {string|number} props.value - Main value to display
 * @param {string} props.icon - Bootstrap icon class (e.g., 'bi-folder')
 * @param {string} props.color - Bootstrap color variant (primary, success, warning, danger, info, secondary)
 * @param {Object} props.trend - Optional trend information
 * @param {string} props.trend.direction - 'up', 'down', or 'neutral'
 * @param {string|number} props.trend.value - Trend value to display
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onClick - Optional click handler
 * @param {boolean} props.loading - When true, shows a loading placeholder instead of `value`.
 *   Prevents the card from showing a misleading "0" while the underlying query is still in flight.
 * @param {string} props['data-testid'] - Test ID for E2E tests
 */
const StatCard = ({
  title,
  value,
  icon,
  color = 'primary',
  trend = null,
  className = '',
  onClick = null,
  loading = false,
  'data-testid': testId,
}) => {
  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return 'bi-arrow-up';
      case 'down':
        return 'bi-arrow-down';
      case 'neutral':
        return 'bi-dash';
      default:
        return 'bi-dash';
    }
  };

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'up':
        return 'text-success';
      case 'down':
        return 'text-danger';
      case 'neutral':
        return 'text-muted';
      default:
        return 'text-muted';
    }
  };

  const cardClasses = `stat-card border-${color} ${onClick ? 'cursor-pointer' : ''} ${className}`;

  // Con `onClick` esto es navegación (se usa así en 7 lugares del
  // AdminDashboard) pero <Card> renderiza un <div>: sin rol, sin foco y sin
  // teclado, o sea inalcanzable para quien no usa mouse y mudo para un lector
  // de pantalla. Se le dan las tres cosas solo cuando es clickeable; sin
  // onClick sigue siendo un <div> decorativo, que es lo correcto.
  const handleKeyDown = (event) => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      // Espacio scrollea la página si no se cancela.
      event.preventDefault();
      onClick(event);
    }
  };

  const interactiveProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onKeyDown: handleKeyDown,
        'aria-label': `${title}: ${loading ? 'cargando' : value}`,
      }
    : {};

  return (
    <Card
      className={cardClasses}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      data-testid={testId}
      {...interactiveProps}
    >
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            {/* 12px era la palabra que dice QUÉ se está contando: el número
                grande no significa nada sin ella. 14px es el piso del
                proyecto. */}
            <p className="text-muted mb-2 text-uppercase fw-semibold" style={{ fontSize: '0.875rem', letterSpacing: '0.5px' }}>
              {title}
            </p>
            <h2 className="mb-0 fw-bold" style={{ fontSize: 'clamp(1.25rem, 1.1vw + 0.7rem, 2rem)', lineHeight: 1.05, overflowWrap: 'anywhere' }}>
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm text-muted"
                  role="status"
                  data-testid={testId ? `${testId}-loading` : undefined}
                >
                  <span className="visually-hidden">Cargando...</span>
                </span>
              ) : (
                value
              )}
            </h2>
            {trend && !loading && (
              <div className={`mt-3 small ${getTrendColor(trend.direction)}`}>
                <i className={`bi ${getTrendIcon(trend.direction)} me-1`}></i>
                <span className="fw-semibold">{trend.value}</span>
              </div>
            )}
          </div>
          <div className={`stat-icon bg-${color} bg-opacity-10 text-${color} rounded-3`} style={{ padding: '1rem' }}>
            <i className={`bi ${icon}`} style={{ fontSize: '2rem' }}></i>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info', 'secondary']),
  trend: PropTypes.shape({
    direction: PropTypes.oneOf(['up', 'down', 'neutral']),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  className: PropTypes.string,
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  'data-testid': PropTypes.string,
};

export default StatCard;
